/**
 * Typed wrapper around the Slack endpoints on the TeamOfOne backend.
 *
 * Dev auth: the backend expects `x-user-id` + `x-location-id` headers until the
 * real IAM v2 guard is wired. We accept identity lookups on the ApiClient so
 * tests and the dev shell can stub them.
 */
import type { ApiClient } from './index'

export interface SlackIdentity {
  userId: string
  locationId: string
}

export type SlackTokenType = 'bot' | 'user'

export interface SlackClientSessionStatus {
  connected: boolean
  capturedAt: string | null
  invalidatedAt: string | null
  slackUserId: string | null
}

export interface SlackConnectionStatus {
  connected: boolean
  tokenType: SlackTokenType | null
  slackUserId: string | null
  teamName: string | null
  userScopes: string[]
  botScopes: string[]
  clientSession: SlackClientSessionStatus
}

export interface CaptureClientSessionInput {
  /** xoxc- bearer token from any chat.postMessage Authorization header. */
  xoxcToken: string
  /** xoxd- value of the `d` cookie on slack.com. */
  xoxdCookie: string
}

export interface CaptureClientSessionResult {
  ok: true
  slackTeamId: string
  slackUserId: string
  capturedAt: string
}

export interface SlackChannelRow {
  id: string
  name: string
  isPrivate: boolean
  isMember: boolean
  topic: string
  numMembers: number
}

/**
 * Channel listings now always come from the *user's* own Slack token (via
 * `users.conversations`) — see backend SlackChannelsController. The `source`
 * literal is kept for forwards-compat in case we re-introduce a bot fallback,
 * but in practice it will always be `'user'`.
 */
export type SlackChannelsSource = 'user'

export interface SlackChannelsResponse {
  source: SlackChannelsSource
  channels: SlackChannelRow[]
  /**
   * Total channels in this response. For full lists this is `channels.length`;
   * for paginated search results it's the count returned on this page.
   */
  total: number
  nextCursor?: string
}

/**
 * Structured 401 returned by the backend when the caller's Slack connection
 * is missing or has stale scopes. Frontends should catch and prompt the user
 * to (re)connect Slack rather than rendering a generic error.
 */
export interface SlackChannelsAuthError {
  code: 'slack_not_connected' | 'slack_reconnect_required'
  message: string
  missingScopes?: string[]
}

export interface ListChannelsOptions {
  q?: string
  limit?: number
  cursor?: string
}

export interface SlackReply {
  ts: string
  slackUserId: string
  text: string
  at: string
}

export interface SlackThread {
  threadId: string
  locationId: string
  userId: string
  taskId: string | null
  strategyId: string | null
  channel: string
  parentTs: string
  title: string
  status: string
  agent: 'claude' | 'codex' | 'cursor' | null
  replies: SlackReply[]
  replyCount: number
  lastActivityAt: string
  createdAt: string
  updatedAt: string
}

export interface PostThreadInput {
  channel: string
  text: string
  title: string
  taskId?: string
  strategyId?: string
  agent?: 'claude' | 'codex' | 'cursor'
  status?: string
}

type Headers = Record<string, string>

function identityHeaders(id: SlackIdentity): Headers {
  return { 'x-user-id': id.userId, 'x-location-id': id.locationId }
}

/**
 * Error thrown by `fetchJson` when the response is not 2xx. Exposes the
 * parsed body so callers can branch on structured error codes (e.g.
 * `slack_not_connected`) without re-fetching.
 */
export class SlackApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message: string,
  ) {
    super(message)
    this.name = 'SlackApiError'
  }
}

// Small helper: call raw fetch with extra headers so we don't have to upgrade
// ApiClient's base signature for every endpoint. Mirrors ApiClient's baseUrl
// handling so behaviour matches the rest of the SDK.
async function fetchJson<T>(
  client: ApiClient,
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  extraHeaders: Headers,
  body?: unknown
): Promise<T> {
  const init: Omit<RequestInit, 'headers'> = { method }
  if (body !== undefined) init.body = JSON.stringify(body)
  const res = await client.fetchWithAuth(path, init, extraHeaders)
  if (!res.ok) {
    let parsed: unknown = null
    try {
      parsed = await res.json()
    } catch {
      // Body was not JSON — leave parsed as null and rely on status text.
    }
    throw new SlackApiError(
      res.status,
      parsed,
      `${method} ${path} failed: ${res.status}`,
    )
  }
  return res.json() as Promise<T>
}

export class SlackApi {
  constructor(private readonly client: ApiClient) {}

  /** Absolute URL the UI should navigate to in order to start OAuth. */
  installUrl(id: SlackIdentity): string {
    const qs = new URLSearchParams({ userId: id.userId, locationId: id.locationId })
    return `${this.client.baseUrl}/slack/oauth/install?${qs.toString()}`
  }

  getConnection(id: SlackIdentity): Promise<SlackConnectionStatus> {
    return fetchJson<SlackConnectionStatus>(
      this.client,
      'GET',
      '/slack/oauth/connections/me',
      identityHeaders(id)
    )
  }

  disconnect(id: SlackIdentity): Promise<{ deleted: boolean }> {
    return fetchJson<{ deleted: boolean }>(
      this.client,
      'DELETE',
      '/slack/oauth/connections/me',
      identityHeaders(id)
    )
  }

  /**
   * Save (or replace) the per-user xoxc/xoxd browser-session credentials
   * captured from a logged-in slack.com browser tab. Backend validates
   * via auth.test, requires the workspace + identity to match the
   * existing OAuth connection, then encrypts and persists.
   */
  setClientCredentials(
    id: SlackIdentity,
    input: CaptureClientSessionInput
  ): Promise<CaptureClientSessionResult> {
    return fetchJson<CaptureClientSessionResult>(
      this.client,
      'POST',
      '/slack/client-credentials',
      identityHeaders(id),
      input
    )
  }

  /** Clear the saved browser-session credentials. OAuth row is untouched. */
  clearClientCredentials(id: SlackIdentity): Promise<{ ok: boolean }> {
    return fetchJson<{ ok: boolean }>(
      this.client,
      'DELETE',
      '/slack/client-credentials',
      identityHeaders(id)
    )
  }

  listChannels(
    id: SlackIdentity,
    options: ListChannelsOptions = {},
  ): Promise<SlackChannelsResponse> {
    const qs = new URLSearchParams()
    if (options.q?.trim()) qs.set('q', options.q.trim())
    if (options.limit) qs.set('limit', String(options.limit))
    if (options.cursor) qs.set('cursor', options.cursor)
    const path = qs.toString() ? `/slack/channels?${qs.toString()}` : '/slack/channels'
    return fetchJson<SlackChannelsResponse>(
      this.client,
      'GET',
      path,
      identityHeaders(id)
    )
  }

  postThread(id: SlackIdentity, input: PostThreadInput): Promise<SlackThread> {
    return fetchJson<SlackThread>(this.client, 'POST', '/slack/threads', identityHeaders(id), input)
  }

  replyToThread(
    id: SlackIdentity,
    threadId: string,
    text: string
  ): Promise<{ ts: string }> {
    return fetchJson<{ ts: string }>(
      this.client,
      'POST',
      `/slack/threads/${encodeURIComponent(threadId)}/reply`,
      identityHeaders(id),
      { text }
    )
  }

  getThread(id: SlackIdentity, threadId: string): Promise<SlackThread> {
    return fetchJson<SlackThread>(
      this.client,
      'GET',
      `/slack/threads/${encodeURIComponent(threadId)}`,
      identityHeaders(id)
    )
  }

  listThreads(
    id: SlackIdentity,
    filter: { taskId?: string; strategyId?: string; userId?: string; limit?: number } = {}
  ): Promise<SlackThread[]> {
    const qs = new URLSearchParams()
    if (filter.taskId) qs.set('taskId', filter.taskId)
    if (filter.strategyId) qs.set('strategyId', filter.strategyId)
    if (filter.userId) qs.set('userId', filter.userId)
    if (filter.limit) qs.set('limit', String(filter.limit))
    const path = qs.toString() ? `/slack/threads?${qs.toString()}` : '/slack/threads'
    return fetchJson<SlackThread[]>(this.client, 'GET', path, identityHeaders(id))
  }
}

export function createSlackApi(client: ApiClient): SlackApi {
  return new SlackApi(client)
}
